import type { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import type { GyroscopeReading } from '../domain/telemetry/raw/GyroscopeReading'
import {
  IMU_LAG_DRIFT_WINDOW_S,
  IMU_LAG_HEADING_HALF_WINDOW_MS,
  IMU_LAG_MAX_MS,
  IMU_LAG_MIN_CORRELATION,
  IMU_LAG_MIN_MOVING_FRACTION,
  IMU_LAG_MIN_RIDING_S,
  IMU_LAG_MIN_SPEED_KMH,
} from '../analysis/config/imuLag'
import { MS_PER_S } from '../utils/timeUnits'

export interface ImuLagEstimate {
  lagMs: number
  correlation: number
}

const RAD_TO_DEG = 180 / Math.PI

const chordHeadingRad = (a: GpsPoint, b: GpsPoint): number =>
  Math.atan2(
    (b.longitude - a.longitude) * Math.cos((a.latitude * Math.PI) / 180),
    b.latitude - a.latitude
  )

/**
 * GPS heading rate per point, deg/s, positive clockwise (the gyro z sign in
 * the bike frame). The heading of the chord from the fix nearest to
 * t − IMU_LAG_HEADING_HALF_WINDOW_MS to the point, against the chord from
 * the point to the fix nearest to t + IMU_LAG_HEADING_HALF_WINDOW_MS. Zero
 * where a chord cannot be formed (stream edges).
 */
export function gpsHeadingRateDegS(gpsPoints: GpsPoint[]): number[] {
  const n = gpsPoints.length
  const out = new Array<number>(n).fill(0)
  const timesMs = gpsPoints.map((p) => p.timestamp.getTime())
  let a = 0
  let b = 0
  for (let i = 0; i < n; i++) {
    const startMs = timesMs[i] - IMU_LAG_HEADING_HALF_WINDOW_MS
    const endMs = timesMs[i] + IMU_LAG_HEADING_HALF_WINDOW_MS
    while (
      a < i &&
      Math.abs(timesMs[a + 1] - startMs) <= Math.abs(timesMs[a] - startMs)
    )
      a++
    while (
      b < n - 1 &&
      Math.abs(timesMs[b + 1] - endMs) < Math.abs(timesMs[b] - endMs)
    )
      b++
    if (a >= i || b <= i) continue
    let dH =
      chordHeadingRad(gpsPoints[i], gpsPoints[b]) -
      chordHeadingRad(gpsPoints[a], gpsPoints[i])
    while (dH > Math.PI) dH -= 2 * Math.PI
    while (dH < -Math.PI) dH += 2 * Math.PI
    const dT = (timesMs[b] - timesMs[a]) / (2 * MS_PER_S)
    out[i] = dT > 0 ? (dH / dT) * RAD_TO_DEG : 0
  }
  return out
}

function medianIntervalMs(gpsPoints: GpsPoint[]): number {
  const deltas: number[] = []
  for (let i = 1; i < gpsPoints.length; i++) {
    deltas.push(
      gpsPoints[i].timestamp.getTime() - gpsPoints[i - 1].timestamp.getTime()
    )
  }
  deltas.sort((a, b) => a - b)
  return deltas[Math.floor(deltas.length / 2)] ?? 0
}

function yawRateDegS(
  gyroPerTick: Array<GyroscopeReading | undefined>
): number[] {
  return gyroPerTick.map((g) =>
    g ? g.z * (g.unit === 'rad/s' ? RAD_TO_DEG : 1) : 0
  )
}

function bestLag(
  gpsPoints: GpsPoint[],
  yaw: number[],
  head: number[],
  intervalMs: number
): ImuLagEstimate | null {
  const moving = gpsPoints.filter(
    (p) => p.speedKmh >= IMU_LAG_MIN_SPEED_KMH
  ).length
  if (moving * intervalMs < IMU_LAG_MIN_RIDING_S * MS_PER_S) return null
  const maxShift = Math.floor(IMU_LAG_MAX_MS / intervalMs)
  let best: ImuLagEstimate | null = null
  for (let s = -maxShift; s <= maxShift; s++) {
    let sxy = 0
    let sxx = 0
    let syy = 0
    for (let i = Math.max(0, -s); i < gpsPoints.length - Math.max(0, s); i++) {
      if (gpsPoints[i].speedKmh < IMU_LAG_MIN_SPEED_KMH) continue
      const x = yaw[i + s]
      const y = head[i]
      sxy += x * y
      sxx += x * x
      syy += y * y
    }
    if (sxx === 0 || syy === 0) continue
    const r = sxy / Math.sqrt(sxx * syy)
    if (!best || r > best.correlation)
      best = { lagMs: s * intervalMs, correlation: r }
  }
  if (!best || best.correlation < IMU_LAG_MIN_CORRELATION) return null
  return best
}

/**
 * Offset of the IMU stream relative to GPS as the per-tick consumers see
 * it: the shift of the calibrated gyro yaw rate that best matches the GPS
 * heading rate. Positive = IMU behind GPS; correct it with
 * shiftTimestamps(imu, -lagMs). A tick's window average trails the tick by
 * half a GPS interval, so an aligned stream reads +interval/2. Null when
 * the yaw correlation is too weak to trust or there is too little riding.
 */
export function estimateImuLag(
  gpsPoints: GpsPoint[],
  gyroPerTick: Array<GyroscopeReading | undefined>
): ImuLagEstimate | null {
  const intervalMs = medianIntervalMs(gpsPoints)
  if (intervalMs <= 0) return null
  return bestLag(
    gpsPoints,
    yawRateDegS(gyroPerTick),
    gpsHeadingRateDegS(gpsPoints),
    intervalMs
  )
}

/**
 * Lag in the first and last riding window of IMU_LAG_DRIFT_WINDOW_S. A
 * session shorter than one window reports its single estimate as both.
 */
export function estimateImuLagDrift(
  gpsPoints: GpsPoint[],
  gyroPerTick: Array<GyroscopeReading | undefined>
): { firstMs: number; lastMs: number } | null {
  const intervalMs = medianIntervalMs(gpsPoints)
  if (intervalMs <= 0) return null
  const yaw = yawRateDegS(gyroPerTick)
  const head = gpsHeadingRateDegS(gpsPoints)
  const windowPts = Math.round((IMU_LAG_DRIFT_WINDOW_S * MS_PER_S) / intervalMs)
  const estimates: ImuLagEstimate[] = []
  if (gpsPoints.length <= windowPts) {
    const movingFraction =
      gpsPoints.filter((p) => p.speedKmh >= IMU_LAG_MIN_SPEED_KMH).length /
      gpsPoints.length
    if (movingFraction < IMU_LAG_MIN_MOVING_FRACTION) return null
    const single = bestLag(gpsPoints, yaw, head, intervalMs)
    return single ? { firstMs: single.lagMs, lastMs: single.lagMs } : null
  }
  for (
    let start = 0;
    start + windowPts <= gpsPoints.length;
    start += windowPts
  ) {
    const slice = gpsPoints.slice(start, start + windowPts)
    const movingFraction =
      slice.filter((p) => p.speedKmh >= IMU_LAG_MIN_SPEED_KMH).length /
      slice.length
    if (movingFraction < IMU_LAG_MIN_MOVING_FRACTION) continue
    const estimate = bestLag(
      slice,
      yaw.slice(start, start + windowPts),
      head.slice(start, start + windowPts),
      intervalMs
    )
    if (estimate) estimates.push(estimate)
  }
  if (estimates.length === 0) return null
  return {
    firstMs: estimates[0].lagMs,
    lastMs: estimates[estimates.length - 1].lagMs,
  }
}

export function shiftTimestamps<T extends { timestamp: Date }>(
  readings: T[],
  deltaMs: number
): T[] {
  return readings.map((r) => ({
    ...r,
    timestamp: new Date(r.timestamp.getTime() + deltaMs),
  }))
}
